import React, { useState, useEffect } from "react";
import getModalStyle from "../../../../util/getModalStyle";
import { useQueryClient } from "react-query";
import round from "../../../../util/round";
import { postAddCost, updateAddCost } from "../../../../api/OrderSummaryApi";
import { AddCostObj } from "../../../../GlobalTypes";

function useAddCostsSelector(
  handleClose: () => void,
  editCost: AddCostObj,
  quoteId: string,
  margin: string,
  withMarkup: boolean,
  hasDealerTax: boolean,
  isFetching: boolean
) {
  const queryClient = useQueryClient();

  const [modalStyle] = useState(() => getModalStyle(50, 50));

  const [cost, setCost] = useState({ amount: "", name: "" });

  const [err, setErr] = useState("");

  const [checked, setChecked] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!editCost?.title && !editCost?.price) return;

    setCost({
      amount: `${editCost.price}`,
      name: editCost.title,
    });
    setChecked(editCost.taxable);
  }, [editCost]);

  const handleCostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value.includes("-")) return;

    if (value.includes(".")) {
      const dec = value.split(".")[1];
      if (dec && dec.length > 2) return;
    }

    if (Number(value) > 1000000) return;

    setCost({ ...cost, [name]: value });
  };

  const handleCancel = () => {
    setCost({ amount: "", name: "" });
    setChecked(true);
    setLoading(false);
    setErr("");
    handleClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChecked(event.target.checked);

  const addCost = async () => {
    if(isFetching) return;

    if (!cost.amount || !cost.name) {
      return setErr("Please Fill Out Both Fields");
    }

    setErr("");
    setLoading(true);

    const response = await postAddCost({
      quoteId,
      addCostName: cost.name,
      addCostAmount: round(Number(cost.amount)),
      taxable: hasDealerTax ? false : checked,
      margin,
      withMarkup
    });

    if (response.data.matchedCount === 1) {
      queryClient.invalidateQueries("Cart");
      handleCancel();
    } else {
      setLoading(false);
      setErr("Error: A Problem Occurred");
    }
  };

  const updateCost = async () => {
    if(isFetching) return;
    
    if (!cost.amount || !cost.name) {
      return setErr("Please Fill Out Both Fields");
    }

    setErr("");
    setLoading(true);

    const response = await updateAddCost({
      quoteId,
      costId: editCost._id || "",
      addCostName: cost.name,
      addCostAmount: round(Number(cost.amount)),
      taxable: hasDealerTax ? false : checked,
      margin,
      withMarkup
    });

    if (response.data.matchedCount === 1) {
      queryClient.invalidateQueries("Cart");
      handleCancel();
    } else {
      setLoading(false);
      setErr("Error: A Problem Occurred");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      return editCost?.price ? updateCost() : addCost();
    }
  };

  const preventEnterEffects = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return {
    preventEnterEffects,
    handleKeyDown,
    updateCost,
    addCost,
    handleChange,
    handleCancel,
    handleCostChange,
    loading,
    checked,
    err,
    modalStyle,
    cost
  };
}

export default useAddCostsSelector;
